import { useCallback, useEffect, useState } from 'react'
import type { Discipline } from '../core/contracts'
import { createRng } from '../core/rng'
import {
  EMPTY_STREAK,
  advanceStreak,
  buildPlan,
  dateKey,
  isComplete,
  markComplete,
  nextGameId,
  visibleStreak,
} from '../core/workout'
import type { Streak, WorkoutState } from '../core/workout'
import { KEYS, store } from '../platform/storage'
import { GAMES } from '../registry'

/**
 * Тренировка дня: загрузка плана, продвижение, серия.
 *
 * План строится один раз в сутки и сохраняется - иначе перезапуск
 * приложения выдавал бы новую программу, пока не выпадет удобная.
 * Вся логика в core/workout, здесь только ввод-вывод.
 */

export interface Workout {
  readonly loading: boolean
  readonly state: WorkoutState | null
  readonly streak: number
  readonly nextGameId: string | null
  readonly done: boolean
  complete: (gameId: string) => Promise<void>
}

/** Seed из даты: программа одна и та же в течение суток. */
function seedFor(date: string): number {
  let h = 0
  for (const ch of date) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return h
}

export function useWorkout(weakest: Discipline | null): Workout {
  const [state, setState] = useState<WorkoutState | null>(null)
  const [streak, setStreak] = useState<Streak>(EMPTY_STREAK)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    void (async () => {
      const today = dateKey()
      let loaded: WorkoutState | null = null
      let savedStreak = EMPTY_STREAK

      try {
        loaded = await store.get<WorkoutState | null>(KEYS.dailyWorkout(today), null)
        savedStreak = await store.get<Streak>(KEYS.streak, EMPTY_STREAK)
      } catch {
        // хранилище недоступно - соберём план заново, не блокируем экран
      }

      // явное ветвление, а не тернарник: через промежуточную булеву
      // переменную TypeScript не сужает тип loaded до непустого
      let fresh: WorkoutState
      if (!loaded || loaded.plan?.date !== today) {
        fresh = {
          plan: buildPlan(GAMES, weakest, createRng(seedFor(today)), today),
          completed: [],
        }
        // сохраняем план сразу, а не при первой отметке: иначе он
        // пересобирался бы при каждом открытии, и порядок упражнений
        // мог бы поехать вслед за изменившейся слабой дисциплиной
        try {
          await store.set(KEYS.dailyWorkout(today), fresh)
        } catch {
          // не сохранилось - план всё равно детерминирован по дате
        }
      } else {
        fresh = loaded
      }

      if (alive) {
        setState(fresh)
        setStreak(savedStreak)
        setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
    // weakest влияет только на первую сборку плана за сутки:
    // готовый план не пересобираем, иначе программа менялась бы на ходу
  }, [weakest])

  const complete = useCallback(
    async (gameId: string) => {
      if (!state) return
      const next = markComplete(state, gameId)
      setState(next)

      try {
        await store.set(KEYS.dailyWorkout(next.plan.date), next)
        if (isComplete(next)) {
          const grown = advanceStreak(streak, next.plan.date)
          setStreak(grown)
          await store.set(KEYS.streak, grown)
        }
      } catch {
        // не сохранилось - прогресс в этой сессии всё равно виден
      }
    },
    [state, streak],
  )

  return {
    loading,
    state,
    streak: visibleStreak(streak),
    nextGameId: state ? nextGameId(state) : null,
    done: state ? isComplete(state) : false,
    complete,
  }
}
